/** Ячейки «Остаток ФБС» и «В пути» со всплывающей карточкой: из чего сложилось число.
 *
 *  Открывается наведением на число или значок ⓘ (с задержкой — чтобы не мигало при проводке
 *  мышью по таблице), кликом и с клавиатуры (Enter/Space на кнопке), на таче — тапом. Открытая
 *  наведением карточка закрывается, когда мышь ушла и с ячейки, и с карточки; открытая кликом —
 *  повторным кликом, Esc или кликом мимо.
 *
 *  Фокус карточка не забирает и не возвращает (`onOpen/CloseAutoFocus` — preventDefault): иначе
 *  наведение на ячейку уводило бы курсор из поля «К перемещению», в котором человек печатает.
 *  Внутри карточки нет ничего интерактивного — фокусировать там нечего.
 *
 *  ⓘ стоит СЛЕВА от числа: числа колонки выровнены вправо и стоят ровно под ИТОГО, где значка нет.
 *  Модели карточек — `fbsCards.ts`.
 */
import { Info } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type * as api from "@/api/supply";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { FbsCardCtx } from "./fbsCards";
import { stockCard, transitCard } from "./fbsCards";
import { DANGER_CLASS } from "./fbsHighlight";
import { fmt, int0 } from "./format";

const OPEN_DELAY_MS = 200;
const CLOSE_DELAY_MS = 150;

/** Число с ⓘ, раскрывающее карточку по наведению, клику, клавиатуре и тапу. */
export function HoverInfo({
  label,
  value,
  valueClassName,
  testId,
  children,
}: {
  /** Доступное имя кнопки («Остаток ФБС: из чего сложился»). */
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  testId?: string;
  /** Содержимое карточки. */
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const via = useRef<"hover" | "click" | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const clearTimers = (): void => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  useEffect(() => clearTimers, []);

  const show = (how: "hover" | "click"): void => {
    clearTimers();
    via.current = how;
    setOpen(true);
  };
  const hide = (): void => {
    clearTimers();
    via.current = null;
    setOpen(false);
  };
  // тач эмулирует pointerenter перед кликом — его наведением не считаем, иначе тап мигал бы
  const onEnter = (e: React.PointerEvent): void => {
    if (e.pointerType === "touch") return;
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (open || openTimer.current !== null) return;
    openTimer.current = window.setTimeout(() => show("hover"), OPEN_DELAY_MS);
  };
  const onLeave = (e: React.PointerEvent): void => {
    if (e.pointerType === "touch") return;
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    openTimer.current = null;
    if (via.current === "hover")
      closeTimer.current = window.setTimeout(hide, CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={(v) => (v ? setOpen(true) : hide())}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          onPointerEnter={onEnter}
          onPointerLeave={onLeave}
          onClick={(e) => {
            // свой переключатель вместо Radix: карточку, открытую наведением, клик закрепляет,
            // а не закрывает
            e.preventDefault();
            if (open && via.current === "click") hide();
            else show("click");
          }}
          className="group inline-flex max-w-full items-center justify-end gap-1 rounded tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Info
            aria-hidden
            className="size-3 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground"
          />
          <span className={cn("decoration-dotted underline-offset-2 group-hover:underline", valueClassName)}>
            {value}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-3 text-xs"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={onEnter}
        onPointerLeave={onLeave}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

function CardTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="border-b pb-1.5 text-sm font-semibold leading-snug">{children}</div>;
}

function CardFooter({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <p className="border-t pt-1.5 text-[11px] leading-snug text-muted-foreground">{children}</p>
  );
}

function CardRow({
  label,
  note,
  qty,
  muted,
  strong,
  qtyClassName,
  title,
}: {
  label: React.ReactNode;
  note?: string;
  qty: string;
  muted?: boolean;
  strong?: boolean;
  qtyClassName?: string;
  title?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        muted && "text-muted-foreground/60",
        strong && "font-semibold",
      )}
      title={title}
    >
      <span className="min-w-0">
        {label}
        {note && <span className="ml-1.5 text-[10px] text-muted-foreground">{note}</span>}
      </span>
      <span className={cn("shrink-0 tabular-nums", qtyClassName)}>{qty}</span>
    </div>
  );
}

/** Карточка «Остаток ФБС»: юрлица комнаты, минус обещанное, итог; у 1С — организация склада. */
export function StockCardBody({
  detail,
  stockQty,
  ctx,
}: {
  detail: api.FbsStockDetail;
  stockQty: number;
  ctx: FbsCardCtx;
}): React.ReactElement {
  const c = stockCard(detail, stockQty, ctx);
  return (
    <div className="space-y-2" data-testid="fbs-stock-card">
      <CardTitle>{c.title}</CardTitle>
      <div className="space-y-1">
        {c.lines.map((l) => (
          <CardRow
            key={l.key}
            label={l.sign === "−" ? `− ${l.label}` : l.label}
            note={l.note}
            qty={`${fmt(l.qty)} шт`}
            muted={l.muted}
            title={l.title}
          />
        ))}
      </div>
      {c.total !== null && (
        <div className="border-t pt-1.5">
          <CardRow
            label="= свободно"
            qty={`${fmt(c.total)} шт`}
            strong
            qtyClassName={c.total < 0 ? DANGER_CLASS : undefined}
          />
        </div>
      )}
      {c.negativeHint && <p className="text-destructive">{c.negativeHint}</p>}
      {c.mismatch && <p className="text-destructive">{c.mismatch}</p>}
      <CardFooter>{c.footer}</CardFooter>
    </div>
  );
}

/** Карточка «В пути»: заказы на перемещение, из которых сложилось число. */
export function TransitCardBody({
  docs,
  transitQty,
  ctx,
}: {
  docs: readonly api.FbsTransitDoc[];
  transitQty: number;
  ctx: FbsCardCtx;
}): React.ReactElement {
  const c = transitCard(docs, transitQty, ctx);
  return (
    <div className="space-y-2" data-testid="fbs-transit-card">
      <CardTitle>{c.title}</CardTitle>
      <div className="space-y-1">
        {c.lines.map((l) => (
          <CardRow key={l.key} label={l.label} qty={`${fmt(l.qty)} шт`} />
        ))}
      </div>
      <div className="border-t pt-1.5">
        <CardRow label="= в пути" qty={`${fmt(c.total)} шт`} strong />
      </div>
      {c.mismatch && <p className="text-destructive">{c.mismatch}</p>}
      <CardFooter>{c.footer}</CardFooter>
    </div>
  );
}

/** Ячейка «Остаток ФБС» с карточкой. Класс числа (подсветка) — у вызывающего. */
export function StockCell({
  row,
  ctx,
  valueClassName,
}: {
  row: { nom_key: string; stock_qty: number; stock_detail: api.FbsStockDetail };
  ctx: FbsCardCtx;
  valueClassName?: string;
}): React.ReactElement {
  return (
    <HoverInfo
      label="Остаток ФБС: из чего сложился"
      value={int0(row.stock_qty)}
      valueClassName={valueClassName}
      testId={`fbs-stock-${row.nom_key}`}
    >
      <StockCardBody detail={row.stock_detail} stockQty={row.stock_qty} ctx={ctx} />
    </HoverInfo>
  );
}

/** Ячейка «В пути» с карточкой. Ничего не едет — просто «0», объяснять нечего. */
export function TransitCell({
  row,
  ctx,
}: {
  row: { nom_key: string; in_transit_qty: number; in_transit_detail: api.FbsTransitDoc[] };
  ctx: FbsCardCtx;
}): React.ReactElement {
  if (row.in_transit_qty === 0 && row.in_transit_detail.length === 0)
    return <span>{int0(0)}</span>;
  return (
    <HoverInfo
      label="В пути: из каких заказов"
      value={int0(row.in_transit_qty)}
      testId={`fbs-transit-${row.nom_key}`}
    >
      <TransitCardBody docs={row.in_transit_detail} transitQty={row.in_transit_qty} ctx={ctx} />
    </HoverInfo>
  );
}
